/**
 * Generate unique offline transaction IDs
 * Format: TXN-{timestamp}-{deviceId}-{random}
 * Example: TXN-1738476800000-A7K9-X3M2P5
 */

const DEVICE_ID_KEY = 'pos_device_id';
const CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excludes ambiguous chars: 0, O, 1, I

/**
 * Get or create a unique device ID (4 characters)
 * Stored in localStorage for persistence across sessions
 */
export function getOrCreateDeviceId() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = generateRandomString(4);
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    console.log(`[OfflineId] Created new device ID: ${deviceId}`);
  }

  return deviceId;
}

/**
 * Generate a random string of specified length
 * Uses crypto.getRandomValues for better randomness
 */
export function generateRandomString(length) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);

  return Array.from(array)
    .map(byte => CHARS[byte % CHARS.length])
    .join('');
}

/**
 * Generate a unique offline transaction ID
 * Combines timestamp, device ID, and random string for uniqueness
 */
export function generateOfflineTransactionId() {
  const timestamp = Date.now();
  const deviceId = getOrCreateDeviceId();
  const random = generateRandomString(6);

  return `TXN-${timestamp}-${deviceId}-${random}`;
}

/**
 * Parse an offline transaction ID into its components
 * Useful for debugging and sorting
 */
export function parseOfflineTransactionId(offlineId) {
  if (!offlineId || !offlineId.startsWith('TXN-')) {
    return null;
  }

  const parts = offlineId.split('-');
  if (parts.length !== 4) {
    return null;
  }

  return {
    prefix: parts[0],
    timestamp: parseInt(parts[1], 10),
    deviceId: parts[2],
    random: parts[3],
    createdAt: new Date(parseInt(parts[1], 10))
  };
}

/**
 * Validate an offline transaction ID format
 */
export function isValidOfflineTransactionId(offlineId) {
  if (!offlineId || typeof offlineId !== 'string') {
    return false;
  }

  const parsed = parseOfflineTransactionId(offlineId);
  if (!parsed) {
    return false;
  }

  // Check timestamp is reasonable (not in future, not too old)
  const now = Date.now();
  const oneYearMs = 365 * 24 * 60 * 60 * 1000;

  if (parsed.timestamp > now + 60000) { // Allow 1 minute clock skew
    return false;
  }

  if (parsed.timestamp < now - oneYearMs) {
    return false;
  }

  // Check device ID length
  if (parsed.deviceId.length !== 4) {
    return false;
  }

  // Check random string length
  if (parsed.random.length !== 6) {
    return false;
  }

  return true;
}

/**
 * Get the current device ID (without creating if not exists)
 */
export function getDeviceId() {
  return localStorage.getItem(DEVICE_ID_KEY);
}

/**
 * Reset the device ID (for testing or troubleshooting)
 */
export function resetDeviceId() {
  const oldId = localStorage.getItem(DEVICE_ID_KEY);
  localStorage.removeItem(DEVICE_ID_KEY);
  const newId = getOrCreateDeviceId();
  console.log(`[OfflineId] Reset device ID: ${oldId} -> ${newId}`);
  return newId;
}

export default {
  generateOfflineTransactionId,
  getOrCreateDeviceId,
  generateRandomString,
  parseOfflineTransactionId,
  isValidOfflineTransactionId,
  getDeviceId,
  resetDeviceId
};
